import { useState, useEffect } from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  useDisclosure,
} from '@heroui/react'
import { httpsCallable } from 'firebase/functions'

import { useToast } from '../../../hooks/useToast'
import { functions } from '../../../firebase'

interface UserStatus {
  uid: string
  email: string
  isActive: boolean
  createdAt: { seconds: number; nanoseconds: number }
  updatedAt: { seconds: number; nanoseconds: number }
  deactivatedBy?: string
  deactivatedAt?: { seconds: number; nanoseconds: number }
  deactivationReason?: string
}

interface ListUsersResponse {
  success: boolean
  users?: UserStatus[]
  error?: string
}

interface SetUserStatusResponse {
  success: boolean
  message?: string
  error?: string
}

export function UserManagement() {
  const [users, setUsers] = useState<UserStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingUser, setUpdatingUser] = useState<string | null>(null)
  const [selectedUser, setSelectedUser] = useState<UserStatus | null>(null)
  const [deactivationReason, setDeactivationReason] = useState('')

  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()

  const loadUsers = async () => {
    try {
      setLoading(true)
      const listUsersFunction = httpsCallable<
        Record<string, never>,
        ListUsersResponse
      >(functions, 'listAllUsersFunction')
      const result = await listUsersFunction({})

      if (result.data.success) {
        setUsers(result.data.users || [])
      } else {
        throw new Error(result.data.error || 'Failed to load users')
      }
    } catch (error) {
      console.error('Error loading users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (user: UserStatus, newStatus: boolean) => {
    if (!newStatus) {
      // Show modal for deactivation reason
      setSelectedUser(user)
      setDeactivationReason('')
      onOpen()
      return
    }

    // Reactivate immediately
    await updateUserStatus(user.uid, true)
  }

  const updateUserStatus = async (
    uid: string,
    isActive: boolean,
    reason?: string
  ) => {
    try {
      setUpdatingUser(uid)
      const setStatusFunction = httpsCallable<
        { uid: string; isActive: boolean; reason?: string },
        SetUserStatusResponse
      >(functions, 'setUserStatusFunction')
      const result = await setStatusFunction({
        uid,
        isActive,
        reason,
      })

      if (result.data.success) {
        toast.success(result.data.message || 'User status updated successfully')
        await loadUsers() // Refresh the list
      } else {
        throw new Error(result.data.error || 'Failed to update user status')
      }
    } catch (error) {
      console.error('Error updating user status:', error)
      toast.error('Failed to update user status')
    } finally {
      setUpdatingUser(null)
      onClose()
    }
  }

  const confirmDeactivation = async () => {
    if (selectedUser) {
      await updateUserStatus(selectedUser.uid, false, deactivationReason)
    }
  }

  const formatDate = (timestamp: { seconds: number; nanoseconds: number }) => {
    return new Date(timestamp.seconds * 1000).toLocaleDateString()
  }

  useEffect(() => {
    loadUsers()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              User Management
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Manage user account status and permissions
            </p>
          </div>
          <Button
            color="primary"
            variant="flat"
            onPress={loadUsers}
            isLoading={loading}
          >
            Refresh
          </Button>
        </CardHeader>
        <CardBody>
          <Table aria-label="User management table">
            <TableHeader>
              <TableColumn>EMAIL</TableColumn>
              <TableColumn>STATUS</TableColumn>
              <TableColumn>CREATED</TableColumn>
              <TableColumn>LAST UPDATED</TableColumn>
              <TableColumn>ACTIONS</TableColumn>
            </TableHeader>
            <TableBody
              items={users}
              isLoading={loading}
              loadingContent="Loading users..."
            >
              {(user: UserStatus) => (
                <TableRow key={user.uid}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{user.email}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {user.uid.slice(0, 8)}...
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={user.isActive ? 'success' : 'danger'}
                      variant="flat"
                      size="sm"
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Chip>
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell>{formatDate(user.updatedAt)}</TableCell>
                  <TableCell>
                    <Switch
                      isSelected={user.isActive}
                      onValueChange={(value: boolean) =>
                        handleStatusChange(user, value)
                      }
                      isDisabled={updatingUser === user.uid}
                      color="success"
                      size="sm"
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Switch>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {users.length === 0 && !loading && (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              No users found
            </div>
          )}
        </CardBody>
      </Card>

      {/* Deactivation Reason Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>Deactivate User Account</ModalHeader>
          <ModalBody>
            <p className="mb-4">
              You are about to deactivate the account for{' '}
              <strong>{selectedUser?.email}</strong>. This will prevent them
              from signing in to the application.
            </p>
            <Input
              label="Reason for deactivation (optional)"
              placeholder="e.g., Terms of service violation, spam, etc."
              value={deactivationReason}
              onValueChange={setDeactivationReason}
              variant="bordered"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={confirmDeactivation}
              isLoading={updatingUser === selectedUser?.uid}
            >
              Deactivate Account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
